import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Typography,
  Space,
  Spin,
  Row,
  Col,
  Divider,
} from "antd";
import { SaveOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import ImageUpload from "../../../components/banners/ImageUpload";
import { noWhitespaceValidator } from "../../../utils/commonFunctions";

const { Title, Text } = Typography;

// The two crops a spotlight carries, and the sizes the rail renders them at.
// Kept here so the add and edit screens cannot drift apart on either number.
export const CIRCLE_SIZE = { width: 320, height: 320 };
export const VERTICAL_SIZE = { width: 360, height: 600 };
// English. The end of the artwork fallback chain — a language with no set of
// its own falls back to this one, crop by crop, so it is the only set this
// screen edits.
export const DEFAULT_LANG_ID = 1;

/**
 * Shared add/edit form for a spotlight.
 *
 * Both screens post the same payload and differ only in what they load first
 * and which verb they call, so the form lives here rather than being copied
 * into each and left to drift.
 */
const SpotlightForm = ({
  mode,
  initialValues,
  initialImageSets,
  loading = false,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  const [circleImage, setCircleImage] = useState("");
  const [verticalImage, setVerticalImage] = useState("");
  // Sets belonging to other languages. Not editable here, but carried through
  // the save: the API replaces a spotlight's artwork wholesale (deleteMany then
  // createMany), so posting only English would delete every other language's
  // set — artwork this screen never showed and the admin never touched.
  const [otherLangSets, setOtherLangSets] = useState([]);
  const [saving, setSaving] = useState(false);

  // Edit loads asynchronously, so the form is filled when the values arrive
  // rather than only on mount.
  useEffect(() => {
    if (initialValues) form.setFieldsValue(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    const sets = Array.isArray(initialImageSets) ? initialImageSets : [];
    const english = sets.find(
      (set) => Number(set.lang_id) === DEFAULT_LANG_ID
    );
    setCircleImage(english?.circle_image || "");
    setVerticalImage(english?.vertical_card_image || "");
    setOtherLangSets(
      sets.filter((set) => Number(set.lang_id) !== DEFAULT_LANG_ID)
    );
  }, [initialImageSets]);

  const handleFinish = async (values) => {
    const payload = {
      // The API falls back to the title when no internal name is given, so an
      // empty field is sent as undefined rather than "".
      spotlight_name: values.spotlight_name?.trim() || undefined,
      spotlight_title: values.spotlight_title?.trim(),
      spotlight_is_active: values.spotlight_is_active ?? "Y",
      // Always sent, including when empty: omitting the key tells the API this
      // is a partial update and leaves the existing artwork alone, which would
      // make clearing an image from this screen impossible.
      images: [
        {
          lang_id: DEFAULT_LANG_ID,
          circle_image: circleImage || null,
          vertical_card_image: verticalImage || null,
        },
        ...otherLangSets.map((set) => ({
          lang_id: Number(set.lang_id),
          circle_image: set.circle_image || null,
          vertical_card_image: set.vertical_card_image || null,
        })),
      ],
    };

    try {
      setSaving(true);
      await onSubmit(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading || saving}>
      <Card>
        <Title level={4} style={{ marginTop: 0, marginBottom: 24 }}>
          {mode === "edit" ? "Edit Spotlight" : "Add Spotlight"}
        </Title>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={{ spotlight_is_active: "Y" }}
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Form.Item
                name="spotlight_title"
                label="Title"
                rules={[
                  { required: true, message: "Please enter a title" },
                  { validator: noWhitespaceValidator },
                ]}
                extra="The caption shown under the circle."
              >
                <Input
                  placeholder="e.g. Munafik: Melangkah"
                  size="large"
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item
                name="spotlight_name"
                label="Internal Name"
                rules={[
                  { required: true, message: "Please enter an internal name" },
                  { validator: noWhitespaceValidator },
                ]}
                extra="Only shown in the CMS, never to viewers."
              >
                <Input
                  placeholder="e.g. Discover rail — Munafik"
                  size="large"
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <Form.Item name="spotlight_is_active" label="Status">
                <Select
                  size="large"
                  options={[
                    { label: "Active", value: "Y" },
                    { label: "Inactive", value: "N" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider />

          <Title level={5} style={{ marginTop: 0, marginBottom: 4 }}>
            Artwork
          </Title>
          <Text type="secondary">
            The circle is what the Discover rail shows; the vertical card is
            used where the rail renders taller.
          </Text>

          <Row gutter={24} style={{ marginTop: 16 }}>
            <Col xs={24} md={12}>
              <ImageUpload
                label={`Circle Image (${CIRCLE_SIZE.width}x${CIRCLE_SIZE.height})`}
                width={CIRCLE_SIZE.width}
                height={CIRCLE_SIZE.height}
                imageType="spotlights"
                value={circleImage}
                onChange={setCircleImage}
              />
            </Col>
            <Col xs={24} md={12}>
              <ImageUpload
                label={`Vertical Card Image (${VERTICAL_SIZE.width}x${VERTICAL_SIZE.height})`}
                width={VERTICAL_SIZE.width}
                height={VERTICAL_SIZE.height}
                imageType="spotlights"
                value={verticalImage}
                onChange={setVerticalImage}
              />
            </Col>
          </Row>

          <Divider />

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                {mode === "edit" ? "Update" : "Save"}
              </Button>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/spotlights")}
                size="large"
              >
                Go Back
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Spin>
  );
};

export default SpotlightForm;
